import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Loader2, MapPin, Gift } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink } from "../../data/content";
import { api } from "../../lib/api";
import { FicheContent } from "./FicheLieu";
import { EmailGate, getMailAcces } from "./EmailGate";
import { Dialog, DialogContent } from "../../components/ui/dialog";

const RUBRIQUE_LABELS = {
  souffler: "Souffler, me reposer",
  "prendre-soin": "Prendre soin de moi",
  activite: "Une activité qui me plaît",
  partager: "Partager avec d'autres femmes",
};

const pinIcon = () => {
  const html = `
    <div style="position:relative;width:28px;height:37px;">
      <svg width="28" height="37" viewBox="0 0 38 50" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 48C19 48 34 29 34 17.5C34 8.2 27.2 2 19 2C10.8 2 4 8.2 4 17.5C4 29 19 48 19 48Z"
              fill="#FBF7EF" stroke="#C6870F" stroke-width="2"/>
        <circle cx="19" cy="17.5" r="6" fill="#C6870F"/>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "pblv-pin", iconSize: [28, 37], iconAnchor: [14, 37] });
};

function FicheModal({ lieu, onClose }) {
  if (!lieu) return null;
  return (
    <Dialog open={!!lieu} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        data-testid="fiche-modal"
        className="max-h-[92vh] gap-0 overflow-y-auto rounded-3xl border-pblv-ink/10 bg-pblv-bg p-0 sm:max-w-2xl"
      >
        <FicheContent lieu={lieu} inModal />
      </DialogContent>
    </Dialog>
  );
}

export const MapParentheses = () => {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const groupRef = useRef(null);
  const selectRef = useRef(() => {});
  const [rubrique, setRubrique] = useState(() => {
    const r = new URLSearchParams(window.location.search).get("rubrique");
    return ["souffler", "prendre-soin", "activite", "partager"].includes(r) ? r : "all";
  });
  const [commune, setCommune] = useState("all");
  const [lieux, setLieux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [gateFor, setGateFor] = useState(null);

  // Accès aux fiches partenaires réservé aux visiteuses ayant laissé leur e-mail (une seule fois).
  const openLieu = (lieu) => {
    const acces = getMailAcces();
    if (!acces) {
      setGateFor(lieu);
      return;
    }
    api.post("/acces", { email: acces.email, experience: lieu.name }).catch(() => {});
    setSelected(lieu);
  };

  selectRef.current = openLieu;

  useEffect(() => {
    let active = true;
    api
      .get("/lieux")
      .then(({ data }) => active && setLieux(data))
      .catch(() => active && setLieux([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { center: [16.22, -61.55], zoom: 10, scrollWheelZoom: false });
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    groupRef.current = L.layerGroup().addTo(map);
    map.on("click", () => map.scrollWheelZoom.enable());
    map.on("mouseout", () => map.scrollWheelZoom.disable());
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const group = groupRef.current;
    const map = mapRef.current;
    if (!map || !group) return;
    group.clearLayers();
    const bounds = [];
    lieux.forEach((lieu) => {
      if (commune !== "all" && lieu.commune !== commune) return;
      if (rubrique !== "all" && !(lieu.rubriques || []).includes(rubrique)) return;
      const marker = L.marker([lieu.lat, lieu.lng], { icon: pinIcon() })
        .bindTooltip(lieu.name, { direction: "top", offset: [0, -50] })
        .on("click", () => selectRef.current(lieu));
      marker.addTo(group);
      bounds.push([lieu.lat, lieu.lng]);
    });
    if (bounds.length) map.fitBounds(bounds, { padding: [50, 50] });
  }, [lieux, commune, rubrique]);

  const RUBRIQUES = [
    { key: "all", label: "Toutes les envies" },
    { key: "souffler", label: "Souffler, me reposer" },
    { key: "prendre-soin", label: "Prendre soin de moi" },
    { key: "activite", label: "Une activité qui me plaît" },
    { key: "partager", label: "Partager avec d'autres femmes" },
  ];

  const communes = Array.from(new Set(lieux.map((l) => l.commune).filter(Boolean))).sort();

  const listeLieux = lieux
    .filter(
      (l) =>
        (commune === "all" || l.commune === commune) &&
        (rubrique === "all" || (l.rubriques || []).includes(rubrique))
    )
    .sort((a, b) => {
      const val = (l) => {
        const m = (l.tarif || "").match(/(\d+)/);
        return m ? parseInt(m[1], 10) : Infinity;
      };
      return val(a) - val(b);
    });

  return (
    <section id="carte" data-testid="carte-section" className="relative bg-pblv-bg py-10 md:py-14">
      <div className="mx-auto max-w-[1400px] px-5 md:px-8">
        <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-pblv-mauve">
                La carte des T'POZ
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl font-medium leading-[1.05] tracking-tight text-pblv-ink md:text-6xl">
                Trouvez une T'POZ
                <br />
                <span className="italic">près de chez vous.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-4 lg:col-start-9">
            <p className="text-lg leading-relaxed text-pblv-inksoft">
              Explorez les lieux et prestations partout en Guadeloupe. Cliquez
              sur un point pour découvrir la fiche détaillée et réserver.
            </p>
          </Reveal>
        </div>

        <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">
          {RUBRIQUES.map((r) => (
            <button
              key={r.key}
              data-testid={`map-rubrique-${r.key}`}
              onClick={() => setRubrique(r.key)}
              className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                rubrique === r.key
                  ? "border-transparent bg-pblv-mauve text-white"
                  : "border-pblv-ink/15 bg-white text-pblv-ink hover:border-pblv-mauve/50"
              }`}
            >
              {r.label}
            </button>
          ))}

          <div className="relative shrink-0">
            <MapPin className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-pblv-mauve" strokeWidth={1.8} />
            <select
              data-testid="map-filter-commune"
              value={commune}
              onChange={(e) => setCommune(e.target.value)}
              className="appearance-none rounded-full border border-pblv-ink/15 bg-white py-2 pl-11 pr-9 text-sm font-semibold text-pblv-ink transition-colors hover:border-pblv-ink/40 focus:border-pblv-mauve focus:outline-none"
            >
              <option value="all">Toutes les communes</option>
              {communes.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <Reveal y={40}>
          <div className="relative">
            <div
              ref={mapEl}
              data-testid="parentheses-map"
              style={{ isolation: "isolate", zIndex: 0 }}
              className="relative h-[460px] w-full overflow-hidden rounded-[2rem] border border-pblv-ink/10 shadow-[0_12px_40px_rgba(45,40,48,0.08)] md:h-[600px]"
            />
            {loading && (
              <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center rounded-[2rem] bg-pblv-bg/60 text-pblv-inksoft backdrop-blur-sm">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Chargement de la carte…
              </div>
            )}
          </div>
        </Reveal>

        <p className="mt-4 text-center text-sm text-pblv-inksoft">
          Vous ne trouvez pas de lieu proche ?{" "}
          <a
            href={waLink("Bonjour, je cherche une T'POZ près de chez moi, pouvez-vous m'orienter ?")}
            target="_blank"
            rel="noreferrer"
            data-testid="map-contact-fallback"
            className="font-semibold text-pblv-mauve underline decoration-pblv-rose underline-offset-4 hover:text-pblv-mauvedark"
          >
            Contactez-nous
          </a>
          , nous vous orientons.
        </p>

        {/* Liste des T'POZ */}
        <div id="liste" className="mt-10 scroll-mt-28" data-testid="lieux-liste">
          <h3 className="font-serif text-2xl font-medium text-pblv-ink md:text-3xl">
            La liste des T'POZ
          </h3>
          <p className="mt-1.5 text-sm text-pblv-inksoft">
            Toutes les expériences du réseau, en un coup d'œil. Cliquez sur une fiche pour tous les détails.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {listeLieux.map((l) => {
              const besoinKey = (l.rubriques || []).find((r) => RUBRIQUE_LABELS[r]);
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => openLieu(l)}
                  data-testid={`liste-card-${l.id}`}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-pblv-ink/10 bg-white text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(45,40,48,0.12)]"
                >
                  <div className="relative overflow-hidden">
                    {l.photos?.[0] && (
                      <img
                        src={l.photos[0]}
                        alt={l.name}
                        loading="lazy"
                        data-testid={`liste-photo-${l.id}`}
                        className="w-full aspect-[3/2] object-cover object-center transition-transform duration-500 group-hover:scale-[1.05]"
                      />
                    )}
                    {besoinKey && (
                      <span
                        data-testid={`liste-besoin-${l.id}`}
                        className="absolute left-3 top-3 inline-flex items-center rounded-full bg-pblv-mauve px-3 py-1 text-[11px] font-semibold text-white shadow-sm"
                      >
                        {RUBRIQUE_LABELS[besoinKey]}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col px-4 py-4">
                    <h4 className="font-serif text-lg leading-snug text-pblv-ink">
                      {l.experience_titre || l.name}
                    </h4>
                    <p className="mt-1 truncate text-sm font-medium text-pblv-inksoft">{l.name}</p>
                    <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-pblv-inksoft">
                      <MapPin className="h-3.5 w-3.5 shrink-0 text-pblv-mauve" strokeWidth={1.8} /> {l.commune}
                    </p>
                    {(l.contribution || []).length > 0 && (
                      <p data-testid={`liste-avantage-${l.id}`} className="mt-2.5 flex items-start gap-1.5 text-sm leading-snug">
                        <Gift className="mt-0.5 h-3.5 w-3.5 shrink-0 text-pblv-mauve" strokeWidth={2} />
                        <span className="text-pblv-inksoft">
                          Avantage T'POZ :{" "}
                          <span className="font-semibold text-pblv-mauve">{l.contribution[0]}</span>
                        </span>
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <FicheModal lieu={selected} onClose={() => setSelected(null)} />
      <EmailGate
        open={Boolean(gateFor)}
        onOpenChange={(v) => !v && setGateFor(null)}
        experience={gateFor?.name || ""}
        onGranted={() => {
          const l = gateFor;
          setGateFor(null);
          if (l) setSelected(l);
        }}
      />
    </section>
  );
};

export default MapParentheses;
